import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  message,
  DatePicker,
} from "antd";
import { HomeOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import { buildSavePayload, validateAmount } from "../pendingCheckLogic";
import { CustomUpload } from "@/hocComponents";
import commonFn from "@/utils/commonFn";

const NewEditModal = forwardRef(({ id, submitInitFn, }, ref) => {
  const [form] = Form.useForm();
  const [type, setType] = useState("add");
  const [modalName, setModalName] = useState("CREATE PENDING CHECK");
  const [vendorPendingCheckId, setVendorPendingCheckId] = useState(null);
  const [modalFlag, setModalFlag] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [filePaths, setFilePaths] = useState([]);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);

  const [vendorList, setVendorList] = useState([]);
  const [vendorOption, setVendorOption] = useState({});
  const [propList, setPropList] = useState([]);
  const [glAcctList, setGlAcctList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [isNeedApprovedFlag, setIsNeedApprovedFlag] = useState(0);

  useImperativeHandle(ref, () => ({
    openModal: ({ type, modalName, vendorPendingCheckId }) => {
      setAttachmentsLoaded(type !== "edit");
      setType(type);
      setModalName(modalName);
      setVendorPendingCheckId(vendorPendingCheckId);
      setModalFlag(true);
    },
  }));

  const afterOpenChangeFn = (flag) => {
    if (flag) {
      getIsNeedsApprovalFn();
      if(['add'].includes(type)){
        getVendorList();
        getPropListFn();
      }
      if(['edit'].includes(type)){
        getDetailFn()
      }
    } else {
      setFilePaths([]);
      setVendorList([]);
      setVendorOption({});
      setPropList([]);
      setGlAcctList([]);
      setBankList([]);
      setUnitList([]);
      setIsNeedApprovedFlag(0);
      form.resetFields();
      setBtnLoading(false);
      setBtnDisabled(false);
    }
  };

  const getIsNeedsApprovalFn = async () => {
    const response = await http.post(api.pendingCheckIsNeedsApproval);
    if (response?.code === "1") {
      setIsNeedApprovedFlag(response?.data || 0);
    }
  };

  // Vendor ld
  const getVendorList = async (content = "") => {
    const response = await http.get(api.vendorSearch, {
      params: { content },
    });
    if (response?.code === "1") {
      const list = (response.data || []).map((item) => ({
        value: item.vendid,
        label: item.vendorId,
        vendorName: item.vendorName,
      }));
      setVendorList(list);
      return list;
    }
    return [];
  };

  const vendorChange = async (value, option) => {
    setVendorOption(option);
    form.setFieldsValue({ vendid: value });
  };

  // Prop
  const getPropListFn = async (content = "") => {
    const response = await http.get(api.pendingCheckProperties, { params: { content } });
    if (response.code === "1") {
      setPropList(response?.data || []);
    }
  };

  const propId = Form.useWatch('propId', form);

  useEffect(() => {
    if (propId) {
      getGlAcctListFn();
      getBankListFn();
      getUnitListFun();
    } else {
      setGlAcctList([]);
      setBankList([]);
      setUnitList([]);
    }
  }, [propId]);

  // GL Account
  const getGlAcctListFn = async (content = "") => {
    const response = await http.get(api.pendingCheckGlAccounts, {params: { propId }});
    if (response.code === "1") {
      setGlAcctList(response?.data || []);
    }
  };

  // Bank
  const getBankListFn = async (content = "") => {
    const response = await http.get(api.bank, { params: { content, propId } });
    if (response.code === "1") {
      setBankList(response?.data || []);
    }
  };

  // Unit
  const getUnitListFun = async (content = "") => {
    const response = await http.get(api.unitSearch, {
      params: { content, propId },
    });
    setUnitList(response?.data || []);
  };

  const getDetailFn = async() => {
    setBtnDisabled(true)
    const response = await http.get(api.pendingCheckShowById, { params: {vendorPendingCheckId} });
    if (response?.code === "1") {
      const { invoiceDate, vendorId, vendid, vendorInfoList = [], propInfoList = [] } = response.data;
      const temp = {
        ...response.data,
        invoiceDate: invoiceDate && dayjs(invoiceDate),
      }
      if(vendorId){
        const vendorListTemp = vendorInfoList.map(item => ({value: item.vendid, label: item.vendorId, vendorName: item.vendorName,}))
        vendorChange(vendid, vendorListTemp[0])
        setVendorList(vendorListTemp);
      } else {
        getVendorList()
      }
      setPropList(propInfoList || []);
      form.setFieldsValue(temp);
    }
    setBtnDisabled(false)
  }

  const submitAddOrEditFn = async (type) => {
    setBtnLoading(true);
    try {
      const payload = buildSavePayload(form.getFieldsValue(), filePaths, type, vendorPendingCheckId, attachmentsLoaded);
      const response = await http.post(type === "add" ? api.pendingCheckAdd : api.pendingCheckUpdate, payload);
      if (response?.code === "1") {
        message.success(`${type === "add" ? "New" : "Edit"} successfully`);
        setModalFlag(false);
        submitInitFn();
      } else {
        commonFn.modalFailedFn(response || { msg: "Unable to save the pending check." });
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const submitFn = async () => {
    if (btnLoading || btnDisabled || (type === "edit" && !attachmentsLoaded)) return;
    form
      .validateFields()
      .then((value) => {
        if (type === "add" || type === "edit") {
          submitAddOrEditFn(type);
        }
      })
      .catch((err) => {
        console.log(err, "===err");
      });
  };

  const testFn = async () => {
    console.log(form.getFieldValue(), "===form.getFieldValue()");
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3c8dbc" }}>
          <HomeOutlined style={{ marginRight: "8px" }} />
          {modalName}
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      centered
      open={modalFlag}
      onCancel={() => !btnLoading && setModalFlag(false)}
      footer={null}
      maskClosable={false}
      afterOpenChange={afterOpenChangeFn}
      width="80%"
      forceRender={true}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelWrap={true}
        labelAlign="right"
        validateMessages={{
          required: "${label} is required!",
        }}
        initialValues={{
          isNeedApproved: 1,
          recurring: "no",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div
              style={{
                marginBottom: "16px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              <HomeOutlined style={{ marginRight: "8px" }} />
              Pending Check Detail
            </div>

            <Form.Item
              name="vendid"
              label="Vendor Id"
              rules={[{ required: true, message: "Vendor Id is required!" }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Select a type"
                onChange={vendorChange}
                onSearch={getVendorList}
                optionLabelProp="value"
                value={vendorOption?.value}
                filterOption={(input, option) =>
                  (option?.vendorName ?? "")
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{ label: "vendorName" }}
                options={vendorList}
              />
              <div>{vendorOption?.vendorName || ""}</div>
            </Form.Item>

            <Form.Item name="customerAccount" label="Customer #">
              <Input placeholder="Please input Customer #" />
            </Form.Item>

            <Form.Item
              name="invoice"
              label="Invoice"
              rules={[{ required: true, message: "Invoice is required!" }]}
            >
              <Input placeholder="Please input Invoice" />
            </Form.Item>

            <Form.Item
              name="invoiceDate"
              label="Invoice Date"
              rules={[{ required: true, message: "Invoice Date is required!" }]}
            >
              <DatePicker style={{ width: "100%" }} format="MM/DD/YYYY" />
            </Form.Item>

            {commonFn.formInputNumberDoc({
              form,
              name: "amount",
              label: "Amount",
              allowNegative: false,
              validator: validateAmount,
            })}

            <Form.Item
              name="propId"
              label="Prop"
              rules={[{ required: true, message: "Prop is required!" }]}
            >
              <Select
                showSearch
                allowClear
                onSearch={getPropListFn}
                onChange={(value, record) => {
                  form.setFieldsValue({
                    bankId: value ? record?.apBankId : null,
                    glAcct: undefined,
                    unitId: undefined,
                    tenantId: 0,
                  });
                }}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.propName ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: "propId",
                  label: "propName",
                }}
                options={propList}
              />
            </Form.Item>

            <Form.Item
              name="glAcct"
              label="GL Account"
              rules={[{ required: true, message: "GL Account is required!" }]}
            >
              <Select
                showSearch
                allowClear
                onSearch={getGlAcctListFn}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.glAcctTitle ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: "glAcct",
                  label: "glAcctTitle",
                }}
                options={glAcctList}
              />
            </Form.Item>

            <Form.Item
              name="bankId"
              label="Bank"
              rules={[{ required: true, message: "Bank is required!" }]}
            >
              <Select
                showSearch
                allowClear
                onSearch={getBankListFn}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.name ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: "bankId",
                  label: "name",
                }}
                options={bankList}
              />
            </Form.Item>

            <Form.Item name="unitId" label="Unit">
              <Select
                showSearch
                allowClear
                onSearch={getUnitListFun}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.unitName ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: "unitId",
                  label: "unitName",
                }}
                options={unitList}
              />
            </Form.Item>

            <Form.Item name="isNeedApproved" label="Needs Approval">
              <Select
                showSearch
                placeholder="Select a type"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[
                  isNeedApprovedFlag && {
                    label: "No",
                    value: 0,
                  },
                  {
                    label: "Yes",
                    value: 1,
                  },
                ].filter(Boolean)}
              />
            </Form.Item>

            <Form.Item
              name="recurring"
              label="Recurring"
              rules={[{ required: true, message: "Recurring is required!" }]}
            >
              <Select
                showSearch
                placeholder="Select a type"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[
                  {
                    label: "No",
                    value: "no",
                  },
                  {
                    label: "Yes",
                    value: "yes",
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="remark"
              label="Remark"
              rules={[{ required: true, message: "Remark is required!" }]}
            >
              <Input placeholder="Please input Remark" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <div
              style={{
                marginBottom: "16px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              <HomeOutlined style={{ marginRight: "8px" }} />
              Upload PDF or Image
            </div>

            <Form.Item
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              name="filePaths"
              label={null}
            >
              <div
                key={vendorPendingCheckId}
                style={{ height: "100%", display: "flex", flexDirection: "column" }}
              >
                <CustomUpload
                  setFilePaths={setFilePaths}
                  onFilesLoaded={setAttachmentsLoaded}
                  filePaths={filePaths} 
                  setBtnDisabled={setBtnDisabled}
                  btnDisabled={btnDisabled}
                  isEdit={['edit'].includes(type)}
                  foreignId={vendorPendingCheckId || undefined}
                  type={'pending_check'}
                  customConfig={{
                    allowAllFiles: true,
                    // pdfHeight: '62vh',
                    // noShowUpload: true,
                    // noSubmitBtn: true,
                    oldStateRemoveIconFlag: false,
                    pageName: 'PendingCheck'
                  }}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div style={{ textAlign: "center" }}>
        <Space align="center">
          {/* <Button key="back" onClick={testFn}>
            TEST
          </Button> */}
          <Button key="back" onClick={() => setModalFlag(false)}>
            Cancel
          </Button>
          {(type !== "view" && (
            <Button
              type="primary"
              onClick={() => submitFn()}
              loading={btnLoading}
              disabled={btnDisabled || (type === "edit" && !attachmentsLoaded)}
            >
              Submit
            </Button>
          )) ||
            null}
        </Space>
      </div>
    </Modal>
  );
});

export default NewEditModal;
